/**
 * Insight Filters - Filtrage avancé pour des milliers d'insights
 */

import React, { useState } from 'react';
import {
  Filter,
  X,
  Search,
  Calendar,
  Tag,
  TrendingUp,
  AlertCircle,
  BarChart3,
  Database,
  Sparkles,
  Star
} from 'lucide-react';
import { InsightType, InsightCategory, InsightFilters as IInsightFilters } from '../../services/BigDataInsightsService';

interface InsightFiltersProps {
  filters: IInsightFilters;
  onFiltersChange: (filters: IInsightFilters) => void;
  onClear: () => void;
  totalInsights: number;
  filteredInsights: number;
}

const InsightFilters: React.FC<InsightFiltersProps> = ({
  filters,
  onFiltersChange,
  onClear,
  totalInsights,
  filteredInsights
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const types: { value: InsightType; label: string; icon: any; color: string }[] = [
    { value: 'pattern', label: 'Patterns', icon: TrendingUp, color: 'blue' },
    { value: 'anomaly', label: 'Anomalies', icon: AlertCircle, color: 'red' },
    { value: 'correlation', label: 'Corrélations', icon: BarChart3, color: 'purple' },
    { value: 'trend', label: 'Tendances', icon: TrendingUp, color: 'green' },
    { value: 'statistical', label: 'Statistiques', icon: Database, color: 'cyan' },
    { value: 'business', label: 'Business', icon: Sparkles, color: 'yellow' },
    { value: 'quality', label: 'Qualité', icon: Star, color: 'pink' }
  ];

  const categories: { value: InsightCategory; label: string; color: string }[] = [
    { value: 'critical', label: 'Critique', color: 'red' },
    { value: 'high_value', label: 'Haute valeur', color: 'purple' },
    { value: 'medium_value', label: 'Valeur moyenne', color: 'blue' },
    { value: 'low_value', label: 'Faible valeur', color: 'gray' },
    { value: 'warning', label: 'Avertissement', color: 'yellow' },
    { value: 'info', label: 'Information', color: 'green' }
  ];

  const toggleType = (type: InsightType) => {
    const currentTypes = filters.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];
    onFiltersChange({ ...filters, types: newTypes });
  };

  const toggleCategory = (category: InsightCategory) => {
    const currentCategories = filters.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    onFiltersChange({ ...filters, categories: newCategories });
  };

  const activeFiltersCount = 
    (filters.types?.length || 0) +
    (filters.categories?.length || 0) +
    (filters.minConfidence !== undefined && filters.minConfidence > 0 ? 1 : 0) +
    (filters.onlyBookmarked ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <div className="bg-theme-surface/30 border border-theme rounded-xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-theme-primary">Filtres</h3>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-bold">
              {activeFiltersCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-theme-secondary">
            {filteredInsights.toLocaleString()} / {totalInsights.toLocaleString()}
          </span>
          {activeFiltersCount > 0 && (
            <button
              onClick={onClear}
              className="p-2 bg-theme-surface text-theme-secondary hover:bg-theme rounded-lg transition-colors"
              title="Effacer tous les filtres"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-3 py-1 bg-theme-surface text-theme-primary rounded-lg hover:bg-theme transition-colors text-sm"
          >
            {isExpanded ? 'Réduire' : 'Étendre'}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-theme-secondary" />
        <input
          type="text"
          placeholder="Rechercher dans les insights..."
          value={filters.search || ''}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="w-full pl-10 pr-4 py-2 bg-theme-surface border border-theme rounded-lg text-theme-primary placeholder-theme-secondary focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Quick Filters Row */}
      <div className="flex items-center space-x-2 flex-wrap gap-2">
        <button
          onClick={() => onFiltersChange({ ...filters, onlyBookmarked: !filters.onlyBookmarked })}
          className={`px-3 py-1 rounded-lg transition-colors inline-flex items-center space-x-1 text-sm ${
            filters.onlyBookmarked
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
              : 'bg-theme-surface text-theme-secondary hover:bg-theme'
          }`}
        >
          <Star className="w-3 h-3" />
          <span>Favoris</span>
        </button>

        <div className="flex items-center space-x-2 px-3 py-1 bg-theme-surface rounded-lg">
          <span className="text-xs text-theme-secondary">Confiance min:</span>
          <input
            type="range"
            min="0"
            max="100"
            value={(filters.minConfidence || 0) * 100}
            onChange={(e) => onFiltersChange({ ...filters, minConfidence: parseInt(e.target.value) / 100 })}
            className="w-24"
          />
          <span className="text-xs text-theme-primary font-mono">
            {Math.round((filters.minConfidence || 0) * 100)}%
          </span>
        </div>
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-theme">
          {/* Types */}
          <div>
            <h4 className="text-sm font-semibold text-theme-primary mb-2">Types</h4>
            <div className="flex flex-wrap gap-2">
              {types.map((type) => {
                const Icon = type.icon;
                const isActive = filters.types?.includes(type.value);
                return (
                  <button
                    key={type.value}
                    onClick={() => toggleType(type.value)}
                    className={`px-3 py-2 rounded-lg transition-all inline-flex items-center space-x-2 ${
                      isActive
                        ? `bg-${type.color}-500/20 text-${type.color}-400 border border-${type.color}-500/50`
                        : 'bg-theme-surface text-theme-secondary hover:bg-theme'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-sm font-semibold text-theme-primary mb-2">Catégories</h4>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const isActive = filters.categories?.includes(category.value);
                return (
                  <button
                    key={category.value}
                    onClick={() => toggleCategory(category.value)}
                    className={`px-3 py-2 rounded-lg transition-all ${
                      isActive
                        ? `bg-${category.color}-500/20 text-${category.color}-400 border border-${category.color}-500/50`
                        : 'bg-theme-surface text-theme-secondary hover:bg-theme'
                    }`}
                  >
                    <span className="text-sm">{category.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightFilters;








